/**
 * @file Map-marker-new-list-item.tsx
 * @module MapMarkerNewListItem
 * @description Atsevišķa marķiera rinda tabulā (jaunā versija).
 *              Automātiski veic lineārās references un adreses iegūšanu no
 *              ĢIS datu servisa pēc ģeometrijas izmaiņām, atbalstot piesaistes
 *              (sticky) režīmu ceļu tīklam un OpenLR kodēšanu.
 */
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Feature, getUid } from "ol";
import { Geometry, MultiLineString, Point, Polygon } from "ol/geom";
import { toLonLat, transform } from "ol/proj";
import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { ENTRYPOINT } from "../../map/Endpoints";
import WKT from "ol/format/WKT";
import { updateUndoRedoHistory } from "../controls/Map-undo-redo-control";
import { MARKER_LAYER } from "../Map-marker";
import { findError, NewFeatureListContext } from "./Map-marker-list-wrapper";
import { Tooltip } from "antd";
import { getHighlightStyle } from "../controls/Draw-styles";
import { LinearRefResponse } from "../store/Streets.repository";
import { setUpdate } from "../controls/Map-marker-storage-manager";

interface Props {
	item: Feature<Geometry>;
	checked: boolean;
	onCheck: () => void;
	onDelete: () => void;
}

export const MapMarkerNewListItem = (props: Props) => {
	const { sticky, tag, onSelect, errors, onError, removeError, onLoading, detour } = useContext(NewFeatureListContext);
	const error = findError(props.item, errors);
	const properties = props.item.getProperties();

	useEffect(() => {
		const feature = props.item;

		if (feature.get("DONT_UPDATE") === true) {
			feature.set("DONT_UPDATE", false, true);
			return;
		}

		if (feature.get("updatedlr")) {
			return;
		}

		const geom = feature.getGeometry();
		onLoading(true);

		if (geom instanceof MultiLineString) {
			if (feature.get("sticky") ?? sticky) {
				const [coords] = geom.getCoordinates(); // LineString
				const points = coords.map((c) => {
					const [x, y] = transform(c, "EPSG:3857", "EPSG:3059");
					return { x, y };
				});

				axios
					.post(ENTRYPOINT + "/lr/getlrandcoords", {
						points: points,
						tag: tag,
					})
					.then((response) => {
						onLoading(false);
						const features = response.data.map((res: LinearRefResponse) => {
							const f = new WKT().readFeature(res.geometry_wkt, {
								dataProjection: "EPSG:3059",
								featureProjection: "EPSG:3857",
							});
							f.setProperties({
								addresses: res.addresses,
								openlr: res.openlr,
								start: res.from_measure,
								end: res.to_measure,
								updatedlr: true,
								DONT_UPDATE: true,
								street: res.route_id,
								detour: detour,
							});
							return f;
						});
						setUpdate();
						MARKER_LAYER.getSource()!.removeFeature(feature);
						MARKER_LAYER.getSource()!.addFeatures(features);

						updateUndoRedoHistory(
							MARKER_LAYER.getSource()!
								.getFeatures()
								.map((f) => f.clone()),
						);
					})
					.catch((e) => {
						feature.set("error", true);
						onError({ ...e.response.data, uid: getUid(props.item) });
					});
			} else {
				axios
					.post(ENTRYPOINT + "/lr/getlrandcoords", {
						line: new WKT().writeGeometry(geom, { dataProjection: "EPSG:3059", featureProjection: "EPSG:3857" }),
						tag: tag,
					})
					.then((response) => {
						onLoading(false);
						const res = response.data[0];
						feature.setProperties(
							{
								addresses: res.addresses,
								openlr: res.openlr,
							},
						);
						updateUndoRedoHistory(
							MARKER_LAYER.getSource()!
								.getFeatures()
								.map((f) => f.clone()),
						);
					})
					.catch((e) => {
						feature.set("error", true);
						onError({ ...e.response.data, uid: getUid(props.item) });
					});
			}
		}

		if (geom instanceof Point && geom.getCoordinates()[0]) {
			const coords = transform(geom.getCoordinates(), "EPSG:3857", "EPSG:3059");
			if (coords[0] && coords[1]) {
				axios
					.post(ENTRYPOINT + "/lr/getlrandcoords", {
						point: { x: coords[0], y: coords[1] },
						tag: tag,
						not_sticky: feature.get("sticky") !== undefined ? !feature.get("sticky") : !sticky,
					})
					.then((response) => {
						onLoading(false);
						const res = response.data[0];
						feature.setProperties({
							addresses: res.addresses,
							openlr: res.openlr,
						});

						if (feature.get("sticky") !== undefined ? feature.get("sticky") : sticky) {
							const point = new WKT().readGeometry(res.geometry_wkt, { dataProjection: "EPSG:3059", featureProjection: "EPSG:3857" }) as Point;
							setUpdate();
							feature.setProperties({ start: res.measure, street: res.route_id, geometry: point, updatedlr: true });
						}
						updateUndoRedoHistory(
							MARKER_LAYER.getSource()!
								.getFeatures()
								.map((f) => f.clone()),
						);
						removeError(props.item);
					})
					.catch((e) => {
						feature.set("error", true);
						onError({ ...e.response.data, uid: getUid(props.item) });
					});
			}
		}

		if (geom instanceof Polygon) {
			axios
				.post(ENTRYPOINT + "/lr/getlrandcoords", {
					polygon: new WKT().writeGeometry(geom, {
						featureProjection: "EPSG:3857",
						dataProjection: "EPSG:3059",
					}),
					tag: tag,
				})
				.then((response) => {
					onLoading(false);
					const features = response.data.map((d: LinearRefResponse) => {
						const f = new WKT().readFeature(d.geometry_wkt, {
							dataProjection: "EPSG:3059",
							featureProjection: "EPSG:3857",
						});
						setUpdate();
						f.setProperties({
							street: d.route_id,
							start: d.from_measure,
							end: d.to_measure,
							DONT_UPDATE: true,
							openlr: d.openlr,
							updatedlr: true,
						});
						return f;
					});
					MARKER_LAYER.getSource()!.removeFeature(feature);
					MARKER_LAYER.getSource()!.addFeatures(features);
				});
		}
	}, [props.item]);

	const getCoords = () => {
		const geometry = props.item.getGeometry();

		if (geometry instanceof MultiLineString) {
			return geometry
				.getCoordinates()
				.map((line) =>
					line.map((coord) => {
						const [lon, lat] = toLonLat(coord);
						return `${+lat.toFixed(6)}, ${+lon.toFixed(6)}`;
					}),
				)
				.join(", ");
		}
		if (geometry instanceof Point) {
			const [lon, lat] = toLonLat(geometry.getCoordinates());
			return `${+lat.toFixed(6)}, ${+lon.toFixed(6)}`;
		}
		return "";
	};

	const getLR = () => {
		const feature = props.item;
		const properties = feature.getProperties();
		const lr = [properties.street, properties.start, properties.end];
		return `${lr[0] ?? ""} ${lr[1] ?? ""}${lr[2] ? "-" + lr[2] : ""}`.trim();
	};

	const coords = getCoords();

	const highlightFeature = () => {
		props.item.setStyle(getHighlightStyle);
	};

	const removeHighlightFeature = () => {
		props.item.setStyle(undefined);
	};

	return (
		<tr
			className={`border-b divide-x hover:bg-[#E6F4FF] transition-all ${!!props.item.get("highlighted") && "bg-[#E6F4FF]"}`}
			onMouseEnter={highlightFeature}
			onMouseLeave={removeHighlightFeature}
		>
			<td className="flex items-center justify-center h-10">
				<input type="checkbox" checked={props.checked} onChange={() => props.onCheck()} />
			</td>
			<Tooltip title={error?.error === "measure_error" ? error.message : ""}>
				<td className={`p-2 truncate ${error?.error === "measure_error" && "!bg-red-100 !text-red-600"}`}>{getLR()}</td>
			</Tooltip>
			{
				<Tooltip title={properties.address?.length > 29 ? properties.address : ""}>
					<td className="p-2 truncate">{properties.address}</td>
				</Tooltip>
			}
			{
				<Tooltip title={error?.error === "point_error" || error?.error === "general_error" ? error.message : coords.slice(0, 120) + (coords.length > 120 ? "..." : "")}>
					<td className={`p-2 truncate ${(error?.error === "point_error" || error?.error === "general_error") && "!bg-red-100 !text-red-600"}`}>{coords}</td>
				</Tooltip>
			}
			<td className="p-2">
				<div className="flex w-full justify-center text-[#1677FF] px-0.5 text-lg gap-2">
					<Tooltip title={"Labot"}>
						<button type="button">
							<EditOutlined onClick={() => onSelect(props.item)} rev={undefined} />
						</button>
					</Tooltip>
					<Tooltip title={"Dzēst"}>
						<button type="button" onClick={() => props.onDelete()}>
							<DeleteOutlined rev={undefined} />
						</button>
					</Tooltip>
				</div>
			</td>
		</tr>
	);
};
